"use client";

import { animated, useSpring } from "@react-spring/web";
import { useGesture } from "@use-gesture/react";
import useEmblaCarousel from "embla-carousel-react";
import { Download, Share2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { HlsVideo } from "@/components/ui/hls-video";
import { shareViaSystemSheet } from "@/lib/native/share";

export interface ChatMediaItem {
  key: string;
  messageId: number;
  kind: "image" | "video";
  src: string;
  posterSrc?: string | null;
  authorName: string | null;
  createdAt: Date;
}

interface ChatMediaViewerProps {
  items: ChatMediaItem[];
  initialIndex: number;
  onClose: () => void;
}

const DISMISS_DISTANCE_PX = 120;
const DISMISS_VELOCITY = 0.5;
const MAX_ZOOM = 4;
const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_ZOOM = 2.4;

const viewerTimeFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function ZoomableImage({
  src,
  alt = "Chat media",
  onRequestClose,
  onZoomChange,
  onTap,
}: {
  src: string;
  alt?: string;
  onRequestClose: () => void;
  onZoomChange: (zoomed: boolean) => void;
  /** A single tap, fired once the double-tap window has passed. */
  onTap?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastTapAtRef = useRef(0);
  const singleTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
    },
    [],
  );
  const [style, api] = useSpring(() => ({
    x: 0,
    y: 0,
    scale: 1,
    config: { tension: 320, friction: 28 },
  }));

  const setZoom = useCallback(
    (scale: number) => {
      onZoomChange(scale > 1.02);
      if (scale <= 1.02) {
        api.start({ scale: 1, x: 0, y: 0 });
      } else {
        api.start({ scale });
      }
    },
    [api, onZoomChange],
  );

  useGesture(
    {
      onPinch: ({ offset: [scale] }) => {
        api.start({ scale: clamp(scale, 1, MAX_ZOOM), immediate: true });
        onZoomChange(scale > 1.02);
      },
      onPinchEnd: ({ offset: [scale] }) => {
        if (scale <= 1.05) setZoom(1);
      },
      onDrag: ({
        pinching,
        cancel,
        last,
        offset: [ox, oy],
        movement: [, my],
        velocity: [, vy],
      }) => {
        if (pinching) {
          cancel();
          return;
        }

        const zoomed = style.scale.get() > 1.02;
        if (zoomed) {
          api.start({ x: ox, y: oy, immediate: !last });
          return;
        }

        // Not zoomed: vertical pull dismisses.
        const pull = Math.max(0, my);
        api.start({ y: pull, immediate: !last });
        if (last) {
          if (
            pull > DISMISS_DISTANCE_PX ||
            (pull > 48 && vy > DISMISS_VELOCITY)
          ) {
            onRequestClose();
          } else {
            api.start({ y: 0 });
          }
        }
      },
      onClick: () => {
        const now = Date.now();
        if (now - lastTapAtRef.current < DOUBLE_TAP_MS) {
          lastTapAtRef.current = 0;
          if (singleTapTimerRef.current) {
            clearTimeout(singleTapTimerRef.current);
            singleTapTimerRef.current = null;
          }
          setZoom(style.scale.get() > 1.02 ? 1 : DOUBLE_TAP_ZOOM);
          return;
        }
        lastTapAtRef.current = now;
        if (onTap) {
          singleTapTimerRef.current = setTimeout(() => {
            singleTapTimerRef.current = null;
            onTap();
          }, DOUBLE_TAP_MS);
        }
      },
    },
    {
      target: containerRef,
      drag: {
        from: () => [style.x.get(), style.y.get()],
        filterTaps: true,
      },
      pinch: { scaleBounds: { min: 1, max: MAX_ZOOM }, rubberband: true },
      eventOptions: { passive: false },
    },
  );

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full touch-none items-center justify-center overflow-hidden"
    >
      {/* biome-ignore lint/performance/noImgElement: full-res zoomable media needs a raw img that react-spring can transform; next/image optimization is bypassed here intentionally. */}
      <animated.img
        src={src}
        alt={alt}
        draggable={false}
        className="max-h-full max-w-full object-contain select-none"
        style={{
          x: style.x,
          y: style.y,
          scale: style.scale,
        }}
      />
    </div>
  );
}

export function ChatMediaViewer({
  items,
  initialIndex,
  onClose,
}: ChatMediaViewerProps) {
  const isZoomedRef = useRef(false);
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: initialIndex,
    loop: false,
    watchDrag: () => !isZoomedRef.current,
  });

  useEffect(() => {
    if (!emblaApi) return;
    const handleSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", handleSelect);
    return () => {
      emblaApi.off("select", handleSelect);
    };
  }, [emblaApi]);

  // Lock background scroll while the viewer is open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") emblaApi?.scrollPrev();
      if (event.key === "ArrowRight") emblaApi?.scrollNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [emblaApi, onClose]);

  const current = items[selectedIndex];

  const handleShare = useCallback(async () => {
    if (!current) return;
    try {
      const response = await fetch(current.src);
      const blob = await response.blob();
      const extension = current.kind === "video" ? "mp4" : "png";
      const file = new File([blob], `hiive-media.${extension}`, {
        type:
          blob.type || (current.kind === "video" ? "video/mp4" : "image/png"),
      });
      const result = await shareViaSystemSheet({
        asset: { file, type: extension },
        title: "Shared from Hiive chat",
      });
      if (!result.ok && result.reason !== "aborted") {
        toast.error("Sharing isn’t available here");
      }
    } catch {
      toast.error("Failed to share media");
    }
  }, [current]);

  const handleDownload = useCallback(() => {
    if (!current) return;
    const anchor = document.createElement("a");
    anchor.href = current.src;
    anchor.download = "";
    anchor.rel = "noopener";
    anchor.target = "_blank";
    anchor.click();
  }, [current]);

  return createPortal(
    <div className="fixed inset-0 z-[110] flex flex-col bg-black">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-black/72 to-transparent pt-[env(safe-area-inset-top)]">
        <div className="pointer-events-auto flex items-center justify-between px-3 py-2.5">
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            aria-label="Close media viewer"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="min-w-0 text-center">
            <div className="truncate text-[13px] font-medium text-white">
              {current?.authorName || "Media"}
            </div>
            {current ? (
              <div className="text-[11px] text-white/64">
                {viewerTimeFormatter.format(current.createdAt)}
              </div>
            ) : null}
          </div>
          <div className="w-9" />
        </div>
      </div>

      <div ref={emblaRef} className="h-full overflow-hidden">
        <div className="flex h-full">
          {items.map((item, index) => (
            <div key={item.key} className="h-full min-w-0 flex-[0_0_100%]">
              {item.kind === "image" ? (
                <ZoomableImage
                  src={item.src}
                  onRequestClose={onClose}
                  onZoomChange={(zoomed) => {
                    if (index === selectedIndex) {
                      isZoomedRef.current = zoomed;
                    }
                  }}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  {/* Only mount the heavy video element near the active slide. */}
                  {Math.abs(index - selectedIndex) <= 1 ? (
                    <HlsVideo
                      controls
                      playsInline
                      preload="metadata"
                      poster={item.posterSrc ?? undefined}
                      src={item.src}
                      className="max-h-full max-w-full"
                    >
                      <track kind="captions" />
                    </HlsVideo>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/72 to-transparent pb-[max(env(safe-area-inset-bottom),0.5rem)]">
        <div className="pointer-events-auto flex items-center justify-between px-4 py-2.5">
          <span className="text-[12px] text-white/64">
            {items.length > 1 ? `${selectedIndex + 1} of ${items.length}` : ""}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleShare()}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              aria-label="Share media"
            >
              <Share2 className="h-4.5 w-4.5" />
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              aria-label="Download media"
            >
              <Download className="h-4.5 w-4.5" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
